import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { JOURNEYS, RETURNING_USER_CHIP } from '../../data/journeyConfig';
import JourneyModal from './JourneyModal';

/**
 * Journey chips - 5 buyer-intent chips under the homepage hero +
 * an optional 6th "I'm Already Building" shortcut for returning users.
 *
 * 2026-02-24: Replaces the old flat 11-chip grid (which has been
 * preserved as sub-chips inside each journey's modal so every
 * existing matcher / resource / explore link is reachable in two
 * clicks).
 *
 *   tone="light"  - for dark/video hero backgrounds (default)
 *   tone="dark"   - for light-page backgrounds
 */
const JourneyChips = ({ tone = 'light', className = '' }) => {
  const [active, setActive] = useState(null);

  const base =
    tone === 'light'
      ? 'bg-white/15 hover:bg-white/25 text-white border-white/25 hover:border-white/50 shadow-[0_2px_10px_rgba(0,0,0,0.18)]'
      : 'bg-white hover:bg-gray-50 text-[#1d1d1f] border-gray-200 hover:border-[#1d1d1f] shadow-sm';

  const chipClass = `inline-flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-full text-[11px] sm:text-[13.5px] font-semibold whitespace-nowrap border transition-colors backdrop-blur-md cursor-pointer ${base}`;

  // Display order = most-used journeys first. Set here (not in the big
  // journeyConfig) so the config stays a stable content source. Any
  // journey not listed is appended so nothing ever drops off.
  const CHIP_ORDER = [
    'build-a-home',
    'renovate-and-extend',
    'improve-my-home',
    'finishing-and-moving-in',
    'invest-and-develop',
    'my-build-needs-help',
  ];
  const orderedJourneys = [
    ...CHIP_ORDER.map((slug) => JOURNEYS.find((j) => j.slug === slug)).filter(Boolean),
    ...JOURNEYS.filter((j) => !CHIP_ORDER.includes(j.slug)),
  ];

  return (
    <>
      {/* Buyer-intent journeys, most-used first, in a compact even
          2-col (mobile) / 3-col (desktop) grid. Uniform single-line
          pills - same height everywhere, slim (no truncation, no
          stretched tiles). Returning-user shortcut centred below. */}
      <div
        className={`mx-auto grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 max-w-sm sm:max-w-2xl ${className}`}
        data-testid="journey-chips"
      >
        {orderedJourneys.map((journey) => (
          <button
            key={journey.slug}
            type="button"
            onClick={() => setActive(journey)}
            className={chipClass}
            data-testid={`journey-chip-${journey.slug}`}
            aria-label={journey.name}
          >
            <img
              src={journey.iconSrc}
              alt=""
              aria-hidden="true"
              className="hidden sm:inline-block w-[14px] h-[14px] sm:w-[22px] sm:h-[22px] object-contain shrink-0"
              loading="lazy"
            />
            <span className="truncate">{journey.name}</span>
          </button>
        ))}
      </div>

      {/* Returning-user shortcut - centred on its own row, visually
          separate from the "start a journey" chips above. */}
      <div className="mt-2.5 sm:mt-3 flex justify-center">
        <Link
          to={RETURNING_USER_CHIP.href}
          className={chipClass}
          data-testid={`journey-chip-${RETURNING_USER_CHIP.slug}`}
          aria-label={RETURNING_USER_CHIP.name}
        >
          <img
            src={RETURNING_USER_CHIP.iconSrc}
            alt=""
            aria-hidden="true"
            className="hidden sm:inline-block w-[14px] h-[14px] sm:w-[22px] sm:h-[22px] object-contain shrink-0"
            loading="lazy"
          />
          <span className="truncate">{RETURNING_USER_CHIP.name}</span>
        </Link>
      </div>

      {active && (
        <JourneyModal journey={active} onClose={() => setActive(null)} />
      )}
    </>
  );
};

export default JourneyChips;
